// @flow
import React, { PureComponent, createRef, type ElementRef } from 'react';
import { Box } from 'gestalt';

const COLORS = ['#4A90E2', '#0FA573', '#B469EB', '#F13535', '#E2780D'];
const PARTICLE_COUNT = 120;
const ANIMATION_DURATION = 800;

type Particle = {
  color: string,
  degree: number,
  friction: number,
  gravity: number,
  opacity: number,
  radius: number,
  speed: number,
  x: number,
  y: number,
  yVel: number,
};

type Props = {
  children: any,
  delay?: number,
  startAnimation: boolean,
};

const randBetween = (min, max) => Math.random() * ((min || 1) - (max || 0)) + (max || 0);
const toRadians = (angle) => angle * (Math.PI / 180);

class DelightfulExplosion extends PureComponent<Props> {
  static defaultProps = {
    delay: 0,
  };

  componentDidUpdate(prevProps: Props) {
    const { delay, startAnimation } = this.props;
    const { startAnimation: prevStartAnimation } = prevProps;
    if (startAnimation && prevStartAnimation !== startAnimation) {
      this.animationTimer = setTimeout(this.explode, delay);
    }
  }

  componentWillUnmount() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }
    if (this.canvasRemovalTimer) {
      clearTimeout(this.canvasRemovalTimer);
    }
  }

  animationTimer: ?TimeoutID = null;
  canvasRemovalTimer: ?TimeoutID = null;
  containerRef: ElementRef<*> = createRef();

  buildCanvas = () => {
    if (!this.containerRef.current) {
      return null;
    }
    const { height, width } = this.containerRef.current.getBoundingClientRect();
    const canvasSize = height * 2.5;
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.left = `${0 - (canvasSize - width) / 2}px`;
    canvas.style.top = `${0 - (canvasSize - height) / 2}px`;
    canvas.style.pointerEvents = 'none';
    canvas.style.width = `${canvasSize}px`;
    canvas.style.height = `${canvasSize}px`;
    canvas.style.overflow = 'visible';
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    return canvas;
  };

  explode = () => {
    const canvas = this.buildCanvas();
    if (!canvas) {
      return;
    }
    const container = this.containerRef.current;
    const ctx = canvas.getContext('2d');
    container.prepend(canvas);
    const particles: Array<Particle> = Array.from(Array(PARTICLE_COUNT)).map((_, index) => ({
      x: canvas.width / 2,
      y: canvas.height / 2,
      radius: Math.round(randBetween(10, 30)),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      degree: Number(randBetween(0, 360).toFixed(1)),
      speed: Math.round(randBetween(0, 25)),
      friction: 0.9,
      opacity: Number(randBetween(0, 0.5).toFixed(1)),
      yVel: 0,
      gravity: 0.04,
    }));
    const render = (particles: Array<Particle>, ctx, width, height) => {
      window.requestAnimationFrame(() => render(particles, ctx, width, height));
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, i) => {
        p.x += p.speed * Math.cos(toRadians(p.degree));
        p.y += p.speed * Math.sin(toRadians(p.degree));

        p.opacity -= 0.01;
        p.speed *= p.friction;
        p.radius *= p.friction;
        p.yVel += p.gravity;
        p.y += p.yVel;

        // early return if particle is faded out or radius is at zero
        if (p.opacity < 0 || p.radius < 0) {
          return;
        }

        ctx.beginPath();
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.arc(p.x, p.y, p.radius, 0, 2 * Math.PI, false);
        ctx.fill();
      });
    };

    render(particles, ctx, canvas.width, canvas.height);
    this.canvasRemovalTimer = setTimeout(() => {
      container.removeChild(canvas);
    }, this.props.delay + ANIMATION_DURATION);
  };

  render() {
    return (
      <Box ref={this.containerRef} position="relative">
        {this.props.children}
      </Box>
    );
  }
}

export default DelightfulExplosion;
